import React, { useEffect } from "react";
import {
  Grant,
  usePendingInvitesListQuery,
  useRemoveUserFromAccountMutation,
  UserInvite,
  useTenantAccessorsQuery,
  useTenantUserPermisionsQuery,
  useUserDataByIdQuery,
  useUsersCountQuery,
  useUsersListQuery
} from "generated/graphql";
import { LocalKeys, useTranslation } from "utilities/i18n";
import {
  infiniteNestLink,
  Switch,
  useHistory,
  Link,
  Route,
  useQueryParamBoolean
} from "polyrouter";
import { Layout } from "views/Main/Layouts";
import { useDispatch, useSelector } from "react-redux";
import { State, useTypesafeActions } from "store";
import {
  HEADER_ID,
  itemCheckedWithSet,
  TableBody,
  TableBulkItemHeader,
  TableSearchEdit,
  TableHeaderItem,
  toggleAll,
  toggleItemWithSet,
  getColumns,
  TableBulkItemRow
} from "collections/Table";
import { DefaultModalForm, useModalDispatch } from "collections/Modal";
import {
  CancelButton,
  CategoryDiv,
  CategorySwitcher,
  LoadingIndicator,
  NoResultsIndicator,
  SubmitButton
} from "elements";
import { changeUsersCursor } from "store/paging";
import { DISMISS, useNotification } from "collections/Notifications";
import { useAuth0 } from "@auth0/auth0-react";
import { ProtectedRoute } from "TsRoleGuards";
import { ToolTip } from "elements/ToolTip";
import MemberDetails from "routes/Admin/Accounts/Members/MemberDetails/MemberDetails";
import { SortState } from "store/filters";
import { TABLES, TABLE_COLUMN_STATE } from "store/userMeta";
import { generateTableHeaders } from "utilities/tableUtils";
import { PendingInvitsListView } from "../../Accounts/Members/PendingInvitesListView";
import VisibilitySensor from "react-visibility-sensor";
import {
  ActionType,
  createStandardAction,
  getType,
  PayloadAC
} from "typesafe-actions";
import InviteMemberModal from "./InviteMemberModal";
import {
  getRoleFromGrants,
  UserDetailProps,
  UserRow
} from "routes/Admin/Accounts/Members/UserRow";
import { RoleName } from "roles";
import { RelevantTenantInfo } from "routes/Admin/Accounts/Members/Helpers";

export type UserTableLabels = {
  userColumnHeader: LocalKeys;
  deleteDialogTitle: LocalKeys;
  deleteDialogContent: LocalKeys;
  deleteDialogConfirm: LocalKeys;
  deleteDialogSuccess: LocalKeys;
};

export type PendingInviteProp = Pick<
  UserInvite,
  "id" | "email" | "tenant_id" | "created_at"
> & { role_name: LocalKeys };

const REFRESH_INTERVAL = 30000;

export const RemoveUserBatchModal: React.FC<{
  tenantId: string;
  members: Array<UserDetailProps>;
  removeIds: Array<string>;
  addToBlackList: PayloadAC<"AddToBlackList", string[]>;
  removeFromBlackList: PayloadAC<"RemoveFromBlackList", string[]>;
  labels: UserTableLabels;
}> = ({
  tenantId,
  members,
  removeIds,
  addToBlackList,
  removeFromBlackList,
  labels
}) => {
  const { t } = useTranslation();
  const returnTo = useModalDispatch();
  const [, dissociateUser] = useRemoveUserFromAccountMutation();
  const [addNotification] = useNotification();

  const removeUsers = async () => {
    let noError = true;

    for (let index = 0; index < removeIds.length; index++) {
      const resp = await dissociateUser({
        id: removeIds[index],
        tid: tenantId
      });
      if (resp.error) {
        noError = false;
        addNotification({
          type: "error",
          id: "delete-members-error",
          message: t("ERROR_DELETING_USER"),
          dismiss: DISMISS.DEFAULT,
          persistThroughNav: true
        });
      }
    }

    if (noError) {
      addNotification({
        type: "success",
        id: "delete-members-success",
        message: t(labels.deleteDialogSuccess),
        dismiss: DISMISS.DEFAULT,
        persistThroughNav: true
      });

      addToBlackList(removeIds);

      window.setTimeout(() => {
        removeFromBlackList(removeIds);
      }, REFRESH_INTERVAL + 1000);
    }
    returnTo();
  };

  const namesToRemove = removeIds.reduce((names, id) => {
    const memberObject = members.find(member => member.user_id === id);
    return memberObject ? [...names, memberObject.name] : names;
  }, [] as Array<string>);

  return (
    <DefaultModalForm
      title={t(labels.deleteDialogTitle, { count: removeIds.length })}
      onSubmit={removeUsers}
      name="RemoveUser"
    >
      {({ isSubmitting }) => (
        <>
          <span data-testid="confirmation">
            {t(labels.deleteDialogContent, { count: removeIds.length })}
          </span>
          <div className="small-text overflow-list modal-body pt-1 pl-1">
            {namesToRemove.map((member, i) => (
              <p key={i} className="list-item">
                {member}
              </p>
            ))}
          </div>
          <div className="btn-container">
            <CancelButton onClick={returnTo} disabled={isSubmitting} />
            <SubmitButton
              isSubmitting={isSubmitting}
              testId="remove"
              labelKey={labels.deleteDialogConfirm}
              submittingLabelKey="DELETING"
            />
          </div>
        </>
      )}
    </DefaultModalForm>
  );
};

export type AccountMembersQueryParams = "pending";

interface UserTableState {
  selectedItems: string[];
  previousSearchTerm?: string;
  memberSearchTerm?: string;
  membersDeleted: boolean;
  usersList: UserDetailProps[];
  pendingInvitesListData: PendingInviteProp[];
  cursor?: number;
  sort: SortState;
  blackList: string[];
}

const Actions = {
  updateSelectedItems: createStandardAction("UpdateSelectedItems")<string[]>(),
  updatePreviousSearchTerm: createStandardAction("UpdatePreviousSearchTerm")<
    string | undefined
  >(),
  updateMemberSearchTerm: createStandardAction("UpdateMemberSearchTerm")<
    string | undefined
  >(),
  updateMembersDeleted: createStandardAction("UpdateMembersDeleted")<boolean>(),
  updateUsersList: createStandardAction("UpdateUsersList")<UserDetailProps[]>(),
  addToBlackList: createStandardAction("AddToBlackList")<string[]>(),
  removeFromBlackList: createStandardAction("RemoveFromBlackList")<string[]>(),
  updatePendingInvitesListData: createStandardAction(
    "UpdatePendingInvitesListData"
  )<PendingInviteProp[]>(),
  updateCursor: createStandardAction("UpdateCursor")<number | undefined>(),
  updateSort: createStandardAction("UpdateSort")<SortState>()
};

type ActionTypes = typeof Actions;

function reducer(
  state: UserTableState,
  action: ActionType<ActionTypes>
): UserTableState {
  switch (action.type) {
    case getType(Actions.updateSelectedItems):
      return {
        ...state,
        selectedItems: action.payload
      };
    case getType(Actions.updatePreviousSearchTerm):
      return {
        ...state,
        cursor: undefined,
        previousSearchTerm: action.payload
      };
    case getType(Actions.updateMemberSearchTerm):
      return {
        ...state,
        memberSearchTerm: action.payload
      };
    case getType(Actions.updateMembersDeleted):
      return {
        ...state,
        membersDeleted: action.payload
      };
    case getType(Actions.updateUsersList):
      return {
        ...state,
        usersList: action.payload
      };
    case getType(Actions.updatePendingInvitesListData):
      return {
        ...state,
        pendingInvitesListData: action.payload
      };
    case getType(Actions.updateCursor):
      return {
        ...state,
        cursor: action.payload
      };
    case getType(Actions.updateSort):
      return {
        ...state,
        sort: action.payload
      };
    case getType(Actions.addToBlackList):
      return {
        ...state,
        blackList: state.blackList.concat(action.payload)
      };
    case getType(Actions.removeFromBlackList):
      return {
        ...state,
        blackList: state.blackList.filter(
          item => !action.payload.includes(item)
        )
      };
  }
}

export const UserTable: React.FC<{
  tenant: RelevantTenantInfo;
  roles: RoleName[];
  tags: string[];
  editingAllowed: boolean;
  labels: UserTableLabels;
}> = ({ tenant, roles, tags, editingAllowed, labels }) => {
  const { t } = useTranslation();
  const { user } = useAuth0();
  const currentUserId = user ? (user.sub as string) : "test";
  const history = useHistory();
  const isMemberDetailView = history.location.pathname.includes(
    "members/details"
  );
  const [pendingListView, setPendingListView] = useQueryParamBoolean<
    AccountMembersQueryParams
  >("pending");
  const { NAME: TABLE_NAME, COLUMNS } = TABLES.MEMBERS;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  const initialState: UserTableState = {
    selectedItems: [],
    previousSearchTerm: undefined,
    memberSearchTerm: undefined,
    membersDeleted: false,
    usersList: [],
    pendingInvitesListData: [],
    blackList: [],
    cursor: undefined,
    sort: {
      field: "name",
      direction: "ASC"
    }
  };

  const [
    {
      selectedItems,
      previousSearchTerm,
      memberSearchTerm,
      usersList,
      pendingInvitesListData,
      blackList,
      cursor,
      sort
    },
    {
      updateSelectedItems,
      updatePreviousSearchTerm,
      updateMemberSearchTerm,
      updateCursor,
      updatePendingInvitesListData,
      updateSort,
      updateUsersList,
      addToBlackList,
      removeFromBlackList
    }
  ] = useTypesafeActions<UserTableState, ActionTypes>(
    reducer,
    initialState,
    Actions
  );

  const dispatch = useDispatch();

  //Returns all users including users with role device_user
  const { data: usersListData, fetching, stale } = useUsersListQuery({
    variables: {
      params: {
        grants: [
          {
            resourceId: tenant.id,
            roles
          }
        ],
        sortBy: sort.field,
        sortByDirection: sort.direction.toLowerCase(),
        searchTerm: previousSearchTerm
      },
      first: 100,
      after: cursor
    },
    requestPolicy: "cache-and-network",
    pollInterval: REFRESH_INTERVAL
  });

  const { data: countData } = useUsersCountQuery({
    variables: {
      params: {
        grants: [
          {
            resourceId: tenant.id,
            roles
          }
        ]
      }
    },
    requestPolicy: "cache-and-network",
    pollInterval: REFRESH_INTERVAL
  });

  //Returns all pending invites
  const {
    data: pendingInvitesListDataRaw,
    fetching: fetchingPendingList,
    stale: stalePendingList,
    executor: pendingInvitesQueryExecutor
  } = usePendingInvitesListQuery({
    variables: {
      tenantId: tenant.id
    },
    requestPolicy: "cache-and-network",
    pollInterval: REFRESH_INTERVAL
  });

  const { data: currentUser, fetching: fetchingUser } = useUserDataByIdQuery({
    variables: {
      id: currentUserId
    },
    requestPolicy: "cache-and-network"
  });

  //Get all users with access to this tenant (includes non-users)
  const { data: rolesData, fetching: rolesFetching } = useTenantAccessorsQuery({
    variables: {
      params: {
        resourceId: tenant.id,
        resourceType: "Tenant"
      }
    },
    requestPolicy: "cache-and-network",
    pollInterval: REFRESH_INTERVAL
  });

  const endCursor = usersListData?.searchResult.pageInfo.endCursor;
  const hasNextPage = usersListData?.searchResult.pageInfo.hasNextPage;

  const { data: accessorData } = useTenantUserPermisionsQuery({
    variables: { userId: currentUserId },
    pause: !user
  });
  const grants = accessorData?.accessor?.grants as Grant[];
  const role = getRoleFromGrants(tenant.id, grants);

  useEffect(() => {
    if (!fetching && previousSearchTerm !== memberSearchTerm) {
      updatePreviousSearchTerm(memberSearchTerm);
    }
  }, [
    fetching,
    previousSearchTerm,
    memberSearchTerm,
    updatePreviousSearchTerm
  ]);

  useEffect(() => {
    // Create an array of objects with member details, role and cursor for actual non device_user users
    const members = new Array<UserDetailProps>();
    usersListData?.searchResult?.searchData.forEach(user => {
      const userGrants =
        rolesData?.accessors.filter(
          accessor => accessor.id === user.node.user_id
        ) || [];

      if (userGrants?.length > 0) {
        userGrants[0]?.grants.forEach(grant => {
          if (grant.resourceId === tenant.id) {
            const userRoles: RoleName[] = grant.roles.map(role => role.name);
            for (const userRole of userRoles) {
              if (roles.includes(userRole)) {
                members.push({
                  ...user.node,
                  cursor: user.cursor,
                  role: userRole
                });
                break;
              }
            }
          }
        });
      }
    });
    updateUsersList(members);
  }, [roles, tenant, rolesData, usersListData, updateUsersList]);

  useEffect(() => {
    let invitesList = new Array<PendingInviteProp>();
    pendingInvitesListDataRaw?.userInvitesByTenant.invites.forEach(invite => {
      const role_name = invite.role_name as LocalKeys;
      if (roles.includes(role_name)) {
        invitesList.push({ ...invite, role_name });
      }
    });
    updatePendingInvitesListData(invitesList);
  }, [tenant, roles, pendingInvitesListDataRaw, updatePendingInvitesListData]);

  useEffect(() => {
    dispatch(changeUsersCursor(undefined));
    updateSelectedItems([]);
  }, [dispatch, updateSelectedItems]);

  const gettingNextPage = fetching || stale || fetchingUser || rolesFetching;

  // Place current member at top of list
  // Additionally, if current user is last in page,
  // swap its cursor with penultimate user to make pagination work
  const currentUserIndex = usersList.findIndex(
    member => member.user_id === currentUserId
  );
  if (currentUserIndex !== -1) {
    const memberObj = usersList[currentUserIndex];
    usersList.splice(currentUserIndex, 1);
    usersList.unshift(memberObj);
    if (memberObj.cursor === endCursor) {
      const lastMember = usersList[usersList.length - 1];
      const tempCursor = memberObj.cursor;
      memberObj.cursor = lastMember.cursor;
      lastMember.cursor = tempCursor;
    }
  } else if (user && currentUser && !fetchingUser && role !== "NONE") {
    usersList.unshift({
      ...currentUser.user.user!,
      cursor: "",
      role
    });
  }

  const memberEmails: string[] = usersList.map(member => member.email);

  const toggleItem = toggleItemWithSet(
    usersList.length,
    selectedItems,
    updateSelectedItems,
    currentUserId
  );

  const itemChecked = itemCheckedWithSet(selectedItems);

  if (
    (!pendingInvitesListData || pendingInvitesListData.length === 0) &&
    pendingListView
  ) {
    setPendingListView(false);
  }

  const { Content } = Layout;
  const selectedExcludingCurrentUser = selectedItems.filter(
    id => id !== HEADER_ID
  );
  const deleteButton =
    selectedExcludingCurrentUser.length > 0 ? (
      <Link
        to={infiniteNestLink("/remove")}
        className="btn"
        data-testid="remove-member"
      >
        {t("DELETE")}
      </Link>
    ) : (
      <button className="btn disabled" data-testid="remove-member" disabled>
        {t("DELETE")}
      </button>
    );

  const getTableHeaders = (column: string, title: string) => {
    switch (column) {
      case COLUMNS.NAME:
      case COLUMNS.EMAIL:
      case COLUMNS.CREATED_AT:
      case COLUMNS.LOGINS_COUNT:
        return (
          <TableHeaderItem
            fieldName={column}
            title={title}
            sort={{
              currentSort: sort,
              onSort: updateSort
            }}
            key={column}
          />
        );
      case COLUMNS.ROLE:
        return (
          <th className="table-header" key={column}>
            <div className="role-header-container">
              <span className="column-header">{t("ROLE")}</span>
              <ToolTip iconTitle={t("ROLES_INFO")} position="bottom">
                <p
                  data-testid="role-description-line"
                  className="role-description-line"
                >
                  <strong>{t("admin")}:</strong> {t("ADMIN_ROLE_DESCRIPTION")}
                </p>
                <p
                  data-testid="role-description-line"
                  className="role-description-line"
                >
                  <strong>{t("it-admin")}:</strong>{" "}
                  {t("DEVICE_MANAGER_ROLE_DESCRIPTION")}
                </p>
                <p
                  data-testid="role-description-line"
                  className="role-description-line"
                >
                  <strong>{t("user")}:</strong> {t("GUEST_ROLE_DESCRIPTION")}
                </p>
              </ToolTip>
            </div>
          </th>
        );
      default:
        return <></>;
    }
  };

  const columnTranslations = {
    [COLUMNS.NAME]: t(labels.userColumnHeader),
    [COLUMNS.EMAIL]: t("EMAIL_ADDRESS"),
    [COLUMNS.CREATED_AT]: t("MEMBER_SINCE"),
    [COLUMNS.LOGINS_COUNT]: t("NO_OF_SESSIONS"),
    [COLUMNS.ROLE]: t("ROLE")
  };

  const onChange = (isVisible: boolean) => {
    if (isVisible) {
      updateCursor(endCursor);
    }
  };

  const filteredUsersList = usersList.filter(
    item => !blackList.includes(item.user_id)
  );

  const columnFilterFn = (column: string) => true;
  const shown = getColumns(shownColumns, columnTranslations, columnFilterFn);
  const hidden = getColumns(hiddenColumns, columnTranslations, columnFilterFn);

  // if user has edit privileges there is one additional column (checkbox for row selection)
  const columnCount = shown.length + (editingAllowed ? 1 : 0);
  const showChecks = editingAllowed && !!shown.length;

  const title = tenant.id === "*" ? t("POLY_USERS") : t("ACCOUNT_MEMBERS");

  return (
    <>
      {!isMemberDetailView && (
        <Content className={tenant.id === "*" ? "no-sidebar" : ""}>
          <div className="card title-card">
            <span className="metric">
              {countData?.searchResult?.count ?? "???"}
            </span>
            <span className="ml-5">{title}</span>
          </div>
          <div>
            <CategorySwitcher data-testid="category-switcher">
              <CategoryDiv
                data-testid="confirmed-option"
                label={
                  hasNextPage
                    ? t("CONFIRMED_PARTIAL_COUNT", {
                        count: filteredUsersList.length
                      })
                    : t("CONFIRMED_COUNT", {
                        count: filteredUsersList.length
                      })
                }
                active={!pendingListView}
                onClick={() => setPendingListView(false)}
              />
              <CategoryDiv
                data-testid="pending-option"
                label={t("PENDING", {
                  count: pendingInvitesListData.length
                })}
                active={!!pendingListView}
                onClick={() => setPendingListView(true)}
                fetching={fetchingPendingList}
                disabled={
                  !pendingInvitesListData || pendingInvitesListData.length === 0
                }
              />
            </CategorySwitcher>
          </div>
          {pendingListView ? (
            <PendingInvitsListView
              tenantId={tenant.id}
              pendingInvitesList={pendingInvitesListData}
              memberCanEdit={editingAllowed}
              fetching={fetchingPendingList}
              stale={stalePendingList}
              executor={pendingInvitesQueryExecutor}
            />
          ) : (
            <>
              <div className="grouping table-actions mb-1">
                {editingAllowed && (
                  <>
                    <Link
                      to={infiniteNestLink("/invite")}
                      className="btn btn-primary"
                      data-testid="add-member"
                    >
                      {t("ADD_NEW")}
                    </Link>
                    {deleteButton}
                  </>
                )}
                <TableSearchEdit
                  search={{
                    onSearch: updateMemberSearchTerm,
                    searchPlaceholder: t("SEARCH_USERS_EMAIL")
                  }}
                  edit={{
                    tableName: TABLE_NAME,
                    shownColumns: shown,
                    hiddenColumns: hidden
                  }}
                />
              </div>
              <div className="table-container">
                <table
                  className="table custom-table"
                  data-testid="member-table"
                >
                  <TableBulkItemHeader
                    showChecks={showChecks}
                    checked={itemChecked(HEADER_ID)}
                    onCheck={() =>
                      toggleAll(
                        filteredUsersList.map(member => member.user_id),
                        selectedItems,
                        updateSelectedItems,
                        currentUserId
                      )
                    }
                  >
                    {generateTableHeaders(shown, getTableHeaders)}
                  </TableBulkItemHeader>
                  <TableBody
                    loading={gettingNextPage && !endCursor}
                    cols={columnCount}
                    accommodateScrollbar
                  >
                    {filteredUsersList.map((member, i) => (
                      <UserRow
                        resourceId={tenant.id}
                        showChecks={showChecks}
                        user={member}
                        checked={itemChecked(member.user_id)}
                        onCheck={() => toggleItem(member.user_id)}
                        canEdit={editingAllowed}
                        index={i}
                        shownColumns={shown.map(column => column.key)}
                        key={`${member.user_id}-${i}`}
                        tags={tags}
                      />
                    ))}

                    {!gettingNextPage && hasNextPage && (
                      <VisibilitySensor
                        offset={{ bottom: -300 }}
                        onChange={onChange}
                      >
                        <TableBulkItemRow
                          showChecks={false}
                          checked={false}
                          onCheck={() => {}}
                        >
                          <td data-testid="Loading">
                            <LoadingIndicator className="inline" />
                          </td>
                        </TableBulkItemRow>
                      </VisibilitySensor>
                    )}
                  </TableBody>
                </table>
                {!fetching && filteredUsersList.length === 0 ? (
                  <NoResultsIndicator />
                ) : null}
              </div>
            </>
          )}
        </Content>
      )}
      <Switch>
        <Route nest path="/invite">
          <InviteMemberModal
            tenant={tenant}
            tags={tags}
            memberEmails={memberEmails}
            executor={pendingInvitesQueryExecutor}
          />
        </Route>
        <ProtectedRoute
          nest
          path="/remove"
          requirement="disassociate:resource"
          render={() => (
            <RemoveUserBatchModal
              tenantId={tenant.id}
              members={filteredUsersList}
              removeIds={selectedExcludingCurrentUser}
              addToBlackList={addToBlackList}
              removeFromBlackList={removeFromBlackList}
              labels={labels}
            />
          )}
        />
        <ProtectedRoute nest path="/details/:user_id" requirement="READ_USERS">
          <MemberDetails />
        </ProtectedRoute>
      </Switch>
    </>
  );
};

export default UserTable;
